import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import { StandInVerdict } from '@/core/data/handshake/handshake.type';
import { localize } from '@/core/gambits';

/**
 * Which positions already have a unique living partner.
 *
 * Positions are offered in map order, oldest first, so an earlier position
 * keeps a person when only one of them can have that person.
 *
 * The same rules are copied in the API's position-matching.ts.
 */
function matchPartners(partners: ReadonlyMap<number, readonly string[]>): Map<string, number> {
  const assigned = new Map<string, number>();

  const visit = (position: number, seen: Set<string>): boolean => {
    for (const partner of partners.get(position) ?? []) {
      if (seen.has(partner)) continue;
      seen.add(partner);

      const takenBy = assigned.get(partner);
      if (takenBy === undefined || visit(takenBy, seen)) {
        assigned.set(partner, position);
        return true;
      }
    }

    return false;
  };

  for (const position of partners.keys()) {
    visit(position, new Set());
  }

  return assigned;
}

/**
 * Moves a person's pink to one of their own positions that has a photo, when
 * the pink one has none and the photo one has nobody. Only free positions are
 * taken, so nobody else moves and the number of pairs stays the same.
 */
function preferPhotoPositions(
  assigned: Map<string, number>,
  partners: ReadonlyMap<number, readonly string[]>,
  withPhoto: ReadonlySet<number>,
): Map<string, number> {
  const taken = new Set(assigned.values());

  for (const [partner, position] of assigned) {
    if (withPhoto.has(position)) continue;

    for (const [candidate, ids] of partners) {
      if (taken.has(candidate) || !withPhoto.has(candidate) || !ids.includes(partner)) continue;

      assigned.set(partner, candidate);
      taken.delete(position);
      taken.add(candidate);
      break;
    }
  }

  return assigned;
}

/**
 * Who stands in for a passerby or ex-member marked unique. Each handshake is
 * its own person, so the matching treats it like any account that played there.
 * The API's position-matching.ts uses the same id.
 */
function standInPartnerId(handshakeId: string): string {
  return `stand-in:${handshakeId}`;
}

/**
 * The empty side of this profile owner's handshake can be reviewed: an
 * unclaimed passerby they made, or an ex-member who left either side.
 */
function isReviewable(handshake: HandshakeDto, userId: string): boolean {
  if (handshake.initiatorId === userId && !handshake.responderId) {
    return !!handshake.isPasserby || !!handshake.isExMember;
  }
  return handshake.responderId === userId && !handshake.initiatorId && !!handshake.isExMember;
}

/** A passerby or ex-member on this profile owner's handshake, marked unique by them. */
export function isReportedStandIn(handshake: HandshakeDto, userId: string): boolean {
  return isReviewable(handshake, userId) && handshake.standInVerdict === StandInVerdict.UNIQUE;
}

/** A passerby or ex-member on this profile owner's handshake, still waiting for their word. */
function isUndecidedStandIn(handshake: HandshakeDto, userId: string): boolean {
  return isReviewable(handshake, userId) && !handshake.standInVerdict;
}

/**
 * The name they were given. Passerby or Ex pair is only said while they wait
 * in the review; without a name, that is all there is to say.
 */
export function standInLabel(handshake: HandshakeDto): string {
  if (handshake.standInName) return handshake.standInName;
  return localize(handshake.isExMember ? 'handshake.standIn.exPair.unnamed' : 'handshake.passerby.unnamed');
}

/** A stand-in on a position. Two can share a name, so the handshake tells them apart. */
export interface StandInMark {
  id: string;
  name: string;
}

/** `reported` is a pair whose person is a passerby or ex-member the owner marked unique, a lighter pink. */
export type PositionTone = 'empty' | 'unique' | 'reported' | 'repeat' | 'unknown';

/** A position photo already on one of the handshakes that played this number. */
export interface CollectedPhoto {
  hash: string;
  handshakeId: string;
  user: { id: string; username: string; profileImageBlobHash: string | null } | null;
  /** When this photo was uploaded. */
  createdAt?: string;
}

export interface CollectedPosition {
  index: number;
  usernames: string[];
  /** Passersby and ex-members marked unique who played this position. */
  standIns: StandInMark[];
  /** The person this position keeps when only one of them can have it. */
  uniqueUsername: string | null;
  /** The passerby or ex-member marked unique this position keeps, when it is theirs. */
  uniqueStandIn: StandInMark | null;
  anonymous: Array<'passerby' | 'unknown'>;
  tone: Exclude<PositionTone, 'empty'>;
  photos: CollectedPhoto[];
}

/** People, each in one group. A person who owns a position is unique. */
export interface PeopleCounts {
  /** Accounts that own one position. Several positions still count as this one person. */
  unique: number;
  /** Passersby and ex-members marked unique that own one position. */
  reported: number;
  /** Has played, and every one of those positions belongs to someone else. */
  repeat: number;
  /** A handshake, and no position on it. */
  none: number;
}

/** A passerby or ex-member handshake that would add a pair if marked unique. */
export interface StandInCandidate {
  handshake: HandshakeDto;
  positions: number[];
}

export interface PositionCollection {
  /** Played positions, in the order the handshakes were given. */
  played: CollectedPosition[];
  tones: PositionTone[];
  /** Positions that have their own person, reported stand-ins included. */
  uniqueCount: number;
  /** Of those, the ones whose person is a reported stand-in. */
  reportedCount: number;
  /** Living partners who own one position. */
  uniquePartnerIds: string[];
  /** Living partners with at least one position played, owned or not. */
  playedPartnerIds: string[];
  people: PeopleCounts;
  /** Every person counted once: accounts, reported stand-ins, and nobody else. */
  peopleTotal: number;
  /** A pair is the shorter side: repeats and people with no position stay on the road. */
  pairCount: number;
  /** Undecided passersby and ex-members that would add a pair if marked unique, oldest first. */
  standInCandidates: StandInCandidate[];
}

const POSITION_COUNT = 960;

/** The profile's positions, coloured by whether each still has its own person. */
export function collectPositions(handshakes: readonly HandshakeDto[], userId: string): PositionCollection {
  const chronological = [...handshakes].sort(
    (left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
  );
  const partners = new Map<number, string[]>();
  const names = new Map<number, string[]>();
  const standInsAt = new Map<number, StandInMark[]>();
  const usernameById = new Map<string, string>();
  const standInById = new Map<string, StandInMark>();
  const anonymous = new Map<number, Array<'passerby' | 'unknown'>>();
  const photos = new Map<number, CollectedPhoto[]>();
  const playedOrder: number[] = [];

  const note = (position: number) => {
    if (!names.has(position)) playedOrder.push(position);
  };

  const addPartner = (position: number, id: string) => {
    const ids = partners.get(position) ?? [];
    if (!ids.includes(id)) ids.push(id);
    if (!partners.has(position)) partners.set(position, ids);
  };

  const addName = (map: Map<number, string[]>, position: number, name: string) => {
    const list = map.get(position) ?? [];
    if (!list.includes(name)) list.push(name);
    if (!map.has(position)) map.set(position, list);
  };

  for (const handshake of chronological) {
    const partner = handshake.initiatorId === userId ? handshake.responder : handshake.initiator;
    const reported = isReportedStandIn(handshake, userId);
    const reportedId = standInPartnerId(handshake.id);
    const reportedMark: StandInMark = { id: reportedId, name: reported ? standInLabel(handshake) : '' };
    if (reported) standInById.set(reportedId, reportedMark);

    for (const position of handshake.positions ?? []) {
      note(position);
      if (!names.has(position)) names.set(position, []);

      if (partner?.id && partner.username) {
        addPartner(position, partner.id);
        usernameById.set(partner.id, partner.username);
        addName(names, position, partner.username);
        continue;
      }

      if (reported) {
        addPartner(position, reportedId);
        const marks = standInsAt.get(position) ?? [];
        if (!marks.some((mark) => mark.id === reportedId)) marks.push(reportedMark);
        if (!standInsAt.has(position)) standInsAt.set(position, marks);
        continue;
      }

      const kind = handshake.isPasserbyPending ? 'passerby' : 'unknown';
      const labels = anonymous.get(position) ?? [];
      if (!labels.includes(kind)) labels.push(kind);
      if (!anonymous.has(position)) anonymous.set(position, labels);
    }

    for (const photo of handshake.photos ?? []) {
      if (photo.positionIndex == null || !(handshake.positions ?? []).includes(photo.positionIndex)) continue;

      const list = photos.get(photo.positionIndex) ?? [];
      const owner =
        photo.userId && photo.userId === handshake.initiatorId
          ? handshake.initiator
          : photo.userId && photo.userId === handshake.responderId
            ? handshake.responder
            : null;
      if (!list.some((item) => item.handshakeId === handshake.id && item.hash === photo.imageBlobHash)) {
        list.push({
          hash: photo.imageBlobHash,
          handshakeId: handshake.id,
          user: owner
            ? {
                id: owner.id,
                username: owner.username,
                profileImageBlobHash: owner.profileImageBlobHash,
              }
            : null,
          ...(photo.createdAt ? { createdAt: photo.createdAt } : {}),
        });
      }
      if (!photos.has(photo.positionIndex)) photos.set(photo.positionIndex, list);
    }
  }

  for (const list of photos.values()) {
    list.sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''));
  }

  const assigned = preferPhotoPositions(matchPartners(partners), partners, new Set(photos.keys()));
  const matched = new Set(assigned.values());
  const matchedPartners = new Set(assigned.keys());
  const ownerUsername = new Map<number, string>();
  const ownerStandIn = new Map<number, StandInMark>();
  for (const [partnerId, position] of assigned) {
    const username = usernameById.get(partnerId);
    if (username) ownerUsername.set(position, username);
    const standIn = standInById.get(partnerId);
    if (standIn) ownerStandIn.set(position, standIn);
  }
  const playedWith = new Set<string>();
  for (const ids of partners.values()) {
    for (const id of ids) playedWith.add(id);
  }

  const met = new Set<string>();
  for (const handshake of chronological) {
    const partner = handshake.initiatorId === userId ? handshake.responder : handshake.initiator;
    if (partner?.id && partner.username) met.add(partner.id);
    else if (isReportedStandIn(handshake, userId)) met.add(standInPartnerId(handshake.id));
  }

  const people: PeopleCounts = { unique: 0, reported: 0, repeat: 0, none: 0 };
  for (const id of met) {
    if (matchedPartners.has(id)) {
      if (standInById.has(id)) people.reported += 1;
      else people.unique += 1;
    } else if (playedWith.has(id)) people.repeat += 1;
    else people.none += 1;
  }

  const byIndex = new Map<number, CollectedPosition>();

  for (const index of playedOrder) {
    const usernames = names.get(index) ?? [];
    const standIns = standInsAt.get(index) ?? [];
    const uniqueStandIn = ownerStandIn.get(index) ?? null;
    let tone: CollectedPosition['tone'] = 'repeat';
    if (matched.has(index)) tone = uniqueStandIn !== null ? 'reported' : 'unique';
    else if (usernames.length === 0 && standIns.length === 0) tone = 'unknown';

    byIndex.set(index, {
      index,
      usernames,
      standIns,
      uniqueUsername: tone === 'unique' ? (ownerUsername.get(index) ?? null) : null,
      uniqueStandIn: tone === 'reported' ? uniqueStandIn : null,
      anonymous: anonymous.get(index) ?? [],
      tone,
      photos: photos.get(index) ?? [],
    });
  }

  const tones: PositionTone[] = Array.from({ length: POSITION_COUNT }, () => 'empty');
  for (const position of byIndex.values()) tones[position.index] = position.tone;

  const displayOrder: number[] = [];
  for (const handshake of handshakes) {
    for (const position of handshake.positions ?? []) {
      if (byIndex.has(position) && !displayOrder.includes(position)) displayOrder.push(position);
    }
  }

  const reportedCount = [...byIndex.values()].filter((position) => position.tone === 'reported').length;

  return {
    played: displayOrder.flatMap((index) => {
      const position = byIndex.get(index);
      return position ? [position] : [];
    }),
    tones,
    uniqueCount: matched.size,
    reportedCount,
    uniquePartnerIds: [...matchedPartners].filter((id) => !standInById.has(id)),
    playedPartnerIds: [...playedWith].filter((id) => !standInById.has(id)),
    people,
    peopleTotal: people.unique + people.reported + people.repeat + people.none,
    pairCount: Math.min(matched.size, people.unique + people.reported),
    standInCandidates: findStandInCandidates(chronological, userId, partners, matched.size),
  };
}

/**
 * Undecided passersby and ex-members that would colour one more square if
 * marked unique: the matching with them standing in is larger than without.
 * One whose positions all belong to people who cannot move is left out.
 */
function findStandInCandidates(
  chronological: readonly HandshakeDto[],
  userId: string,
  partners: ReadonlyMap<number, readonly string[]>,
  matchedCount: number,
): StandInCandidate[] {
  return chronological.flatMap((handshake) => {
    const positions = [...new Set(handshake.positions ?? [])];
    if (!isUndecidedStandIn(handshake, userId) || !positions.length) return [];

    const standIn = standInPartnerId(handshake.id);
    const withStandIn = new Map<number, readonly string[]>(partners);
    for (const position of positions) withStandIn.set(position, [...(partners.get(position) ?? []), standIn]);

    return matchPartners(withStandIn).size > matchedCount ? [{ handshake, positions }] : [];
  });
}
